import { Action, createReducer, on } from '@ngrx/store';
import { DocumentReviewerStep } from 'src/app/compliance/enums/document-reviewer-step.enum';
import {
    contractSelected,
    loadCandidateDocumentByDocumentIdFailure,
    loadCandidateDocumentByDocumentIdSuccess,
    loadDocumentAsPdfFailure,
    loadDocumentAsPdfSuccess,
    loadDocumentDetailsFailure,
    loadDocumentDetailsSuccess,
    loadDocumentIdsFailure,
    loadDocumentIdsSuccess,
    loadDocumentTypeTextRequirementsSuccess,
    loadDocumentTypeRequirementDescriptionsSuccess,
    rejectedDocumentPages,
    rejectedDocumentSuccess,
    resetDocumentTypes,
    resetReviewer,
    resetReviewerInfo,
    searchDocumentTypesByNameFailure,
    searchDocumentTypesByNameSuccess,
    selectedDocumentId,
    selectedDocumentType,
    loadCandidateLiaisonNameByContractIdSuccess,
    resetCandidateLiaisonName,
    setAssignedUploaderId,
    setCandidateId,
    setCurrentReviewerStep,
    setIsCandidateUpload,
    setIsSignedContract,
    setMergeOption,
    setPagesRendered,
    setPreviousReviewerStep,
    setReassignInLegacy,
    setDocumentStatusMovedToWaste,
    setAllPagesSelectedStatus,
    noteAdded,
    loadVaccineManufacturersSuccess,
    setVaccineManufacturer,
    setVaccineDate,
    setVaccineExemption,
    setFormStatus,
    setDoseLotNum,
    setDoseEstablishment,
    setDoseCity,
    setDoseStateId,
    setCandidateDocumentHistory,
    confirmHasBeenClicked,
    rejectedDocumentArgs
} from 'src/app/compliance/store/actions/document-reviewer.actions';
import {
    assignCandidateDocumentTypeFailure,
    assignCandidateDocumentTypeSuccess,
    assignCandidateDocumentPages,
    updateCandidateVaccineInformationFailure,
    updateCandidateVaccineInformation,
    assignCandidateDocumentTypeWithArgs
} from 'src/app/compliance/store/actions/document-upload-tool.actions';
import { DocumentReviewerState } from 'src/app/compliance/store/state/document-reviewer.state';

const initialState: DocumentReviewerState = {
    details: undefined,
    candidateId: 0,
    assignedUploaderId: 0,
    isSignedContract: false,
    isCandidateUpload: false,
    documentIds: [],
    selectedDocumentId: 0,
    candidateDocument: undefined,
    document: undefined,
    contracts: undefined,
    selectedContract: undefined,
    documentTypes: undefined,
    selectedDocumentType: undefined,
    candidateLiaisonName: undefined,
    textRequirements: undefined,
    textRequirementDescriptions: undefined,
    assignDocumentResult: undefined,
    documentPages: undefined,
    mergeChoice: 0,
    readonlyView: false,
    currentReviewerStep: DocumentReviewerStep.ContractSelection,
    previousReviewerStep: DocumentReviewerStep.ContractSelection,
    reAssignedPagesinLegacy: false,
    rejectedDocumentSuccess: false,
    rejectedDocumentArgs: undefined,
    isAllPagesSelected: true,
    vaccineManufacturers: undefined,
    candidateVaccine: undefined,
    isValidForm: true,
    selectedDocumentHistory: undefined,
    error: undefined,
    submitBtnClicked: false,
    confirmBtnClicked: false
};

const reducer = createReducer(
    initialState,
    on(setFormStatus, (state, action) => ({
        ...state,
        isValidForm: action.isValid
    })),
    on(setDoseStateId, (state, action) => ({
        ...state,
        candidateVaccine: {
            ...state.candidateVaccine,
            stateId: action.value
        }
    })),
    on(setDoseCity, (state, action) => ({
        ...state,
        candidateVaccine: {
            ...state.candidateVaccine,
            city: action.value
        }
    })),
    on(setDoseEstablishment, (state, action) => ({
        ...state,
        candidateVaccine: {
            ...state.candidateVaccine,
            establishment: action.value
        }
    })),
    on(setDoseLotNum, (state, action) => ({
        ...state,
        candidateVaccine: {
            ...state.candidateVaccine,
            lotNumber: action.value
        }
    })),
    on(setVaccineDate, (state, action) => ({
        ...state,
        candidateVaccine: {
            ...state.candidateVaccine,
            issuedDate: action.date
        }
    })),
    on(setVaccineExemption, (state, action) => ({
        ...state,
        candidateVaccine: {
            ...state.candidateVaccine,
            issuedDate: action.date // same prop used for both setVaccineDate/setVaccineExemption
        }
    })),
    on(setVaccineManufacturer, (state, action) => ({
        ...state,
        candidateVaccine: {
            ...state.candidateVaccine,
            vaccineManufacturerId: action.manufacturer
        }
    })),

    on(loadVaccineManufacturersSuccess, (state, action) => ({
        ...state,
        vaccineManufacturers: action.manufacturers
    })),

    on(setAllPagesSelectedStatus, (state, action) => ({
        ...state,
        isAllPagesSelected: action.status
    })),

    on(rejectedDocumentSuccess, (state, action) => ({
        ...state,
        rejectedDocumentSuccess: action.success,
        note: ''
    })),

    on(rejectedDocumentPages, (state, action) => ({
        ...state,
        details: {
            ...state.details,
            rejectedPageCount: action.pages.length
        }
    })),

    on(setCandidateId, (state, action) => ({
        ...state,
        candidateId: action.candidateId
    })),

    on(setAssignedUploaderId, (state, action) => ({
        ...state,
        assignedUploaderId: action.assignedUploaderId
    })),

    on(setIsSignedContract, (state, action) => ({
        ...state,
        isSignedContract: action.isSignedContract
    })),

    on(setIsCandidateUpload, (state, action) => ({
        ...state,
        isCandidateUpload: action.isCandidateUpload
    })),

    on(loadDocumentDetailsSuccess, (state, action) => ({
        ...state,
        details: {
            ...action.details,
            isDocumentMovedToWasteStatus: false
        }
    })),

    on(loadDocumentDetailsFailure, (state) => ({
        ...state,
        details: undefined
    })),

    on(loadDocumentIdsSuccess, (state, action) => ({
        ...state,
        documentIds: action.documentIds
    })),

    on(loadDocumentIdsFailure, (state) => ({
        ...state,
        documentIds: []
    })),

    on(selectedDocumentId, (state, action) => ({
        ...state,
        selectedDocumentId: action.documentId
    })),

    on(loadCandidateDocumentByDocumentIdSuccess, (state, action) => ({
        ...state,
        candidateDocument: action.candidateDocument
    })),

    on(loadCandidateDocumentByDocumentIdFailure, (state) => ({
        ...state,
        candidateDocument: undefined
    })),

    on(loadDocumentAsPdfSuccess, (state, action) => ({
        ...state,
        document: [...action.document]
    })),

    on(loadDocumentAsPdfFailure, (state) => ({
        ...state,
        document: undefined
    })),

    on(contractSelected, (state, action) => ({
        ...state,
        selectedContract: action.contract
    })),

    on(setCandidateDocumentHistory, (state, action) => ({
        ...state,
        selectedDocumentHistory: action.value
    })),

    on(noteAdded, (state, action) => ({
        ...state,
        note: action.note
    })),

    on(searchDocumentTypesByNameSuccess, (state, action) => ({
        ...state,
        documentTypes: action.types
    })),

    on(searchDocumentTypesByNameFailure, (state) => ({
        ...state,
        documentTypes: []
    })),

    on(resetDocumentTypes, (state) => ({
        ...state,
        documentTypes: []
    })),

    on(loadDocumentTypeTextRequirementsSuccess, (state, action) => ({
        ...state,
        textRequirements: action.documentTextRequirements
    })),

    on(loadDocumentTypeRequirementDescriptionsSuccess, (state, action) => ({
        ...state,
        textRequirementDescriptions: action.documentTypeRequirementDescriptions
    })),

    on(resetReviewer, () => ({
        ...initialState
    })),

    on(resetReviewerInfo, (state) => ({
        ...state,
        selectedContract: undefined,
        selectedDocumentType: undefined,
        selectedStateLicense: undefined,
        selectedExpirationDate: undefined,
        assignDocumentResult: undefined,
        candidateLiaisonName: undefined,
        candidateVaccine: undefined
    })),

    on(assignCandidateDocumentTypeWithArgs, (state, action) => ({
        ...state,
        submitBtnClicked: true
    })),

    on(assignCandidateDocumentTypeSuccess, (state, action) => ({
        ...state,
        assignDocumentResult: action.assignResult,
        mergeChoice: 0,
        submitBtnClicked: false,
        confirmBtnClicked: false
    })),

    on(assignCandidateDocumentTypeFailure, state => ({
        ...state,
        submitBtnClicked: false,
        confirmBtnClicked: false
    })),

    on(assignCandidateDocumentPages, (state, action) => ({
        ...state,
        details: {
            ...state.details,
            contractId: state.selectedContract.id,
            assignedPageCount: action.pageCount
        }
    })),

    on(assignCandidateDocumentTypeFailure, (state) => ({
        ...state,
        assignDocumentResult: undefined
    })),

    on(setPagesRendered, (state, action) => ({
        ...state,
        documentPages: action.pages
    })),

    on(setMergeOption, (state, action) => ({
        ...state,
        mergeChoice: action.mergeOption
    })),

    on(selectedDocumentType, (state, action) => ({
        ...state,
        selectedDocumentType: action.documentType
    })),

    on(loadCandidateLiaisonNameByContractIdSuccess, (state, action) => ({
        ...state,
        candidateLiaisonName: action.candidateLiaison?.firstName
            ? `${action.candidateLiaison?.firstName} ${action.candidateLiaison?.lastName}`
            : 'N/A'
    })),

    on(resetCandidateLiaisonName, (state) => ({
        ...state,
        candidateLiaisonName: undefined
    })),

    on(setCurrentReviewerStep, (state, action) => ({
        ...state,
        currentReviewerStep: action.step
    })),

    on(setPreviousReviewerStep, (state, action) => ({
        ...state,
        previousReviewerStep: action.step
    })),

    on(setReassignInLegacy, (state, action) => ({
        ...state,
        reAssignedPagesinLegacy: action.reassign
    })),

    on(setDocumentStatusMovedToWaste, (state, action) => ({
        ...state,
        details: {
            ...state.details,
            isDocumentMovedToWasteStatus: action.status
        }
    })),

    on(updateCandidateVaccineInformation, (state) => ({
        ...state,
        error: undefined
    })),

    on(updateCandidateVaccineInformationFailure, (state, action) => ({
        ...state,
        error: action.error
    })),

    on(confirmHasBeenClicked, (state) => ({
        ...state,
        confirmBtnClicked: true
    })),

    on(rejectedDocumentArgs, (state, action) => ({
        ...state,
        rejectedDocumentArgs: action.rejectedDocumentArgs
    }))
);

export function documentReviewerReducer(state: DocumentReviewerState | undefined, action: Action) {
    return reducer(state, action);
}
